import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { getAdminConfig, saveAdminConfigKey, saveAdminConfigBatch } from '../api/client.js';
import { setBootStage } from '../lib/bootStage.js';
import { INCIDENT_CATEGORIES } from '../lib/eventVocabularies.js';
import { migrateSubtypeDeclarations } from '@shared/incidentCategoryCascade.js';
import { DEFAULT_REQUIREMENT_TYPES } from '@shared/requirements.js';
import { TREATMENT_SUBMIT_NOTIFY } from '@shared/notificationCatalog.js';
import { ROLE_CAPABILITIES } from '../auth/roles.js';
import { BUILT_IN_ROLE_PRIORITIES } from '@shared/roleSchema.js';
import { SLA_DEFAULTS } from '@shared/configSchema.js';

function seededBuiltInRole(key, label, value, description, scope) {
  const caps = ROLE_CAPABILITIES[value];
  const defaultScope = value === 'HoRO'
    ? { type: 'assignedRegion' }
    : value === 'ClinicManager'
      ? { type: 'assignedClinics' }
      : { type: 'all' };
  return {
    key, label, value, description, builtIn: true, enabled: true,
    priority: BUILT_IN_ROLE_PRIORITIES[value],
    capabilities: caps ? { ...caps } : undefined,
    scope: scope || defaultScope,
  };
}

export const DEFAULT_CONFIG = {
  // ── Case Types ────────────────────────────────────────────────────────────────
  severityCodes: [
    { key: 'code-1', label: 'Code 1', value: 'Code 1', description: 'Mild',                cssClass: 'sev-1', order: 1 },
    { key: 'code-2', label: 'Code 2', value: 'Code 2', description: 'Moderate',             cssClass: 'sev-2', order: 2 },
    { key: 'code-3', label: 'Code 3', value: 'Code 3', description: 'Severe',               cssClass: 'sev-3', order: 3 },
    { key: 'code-4', label: 'Code 4', value: 'Code 4', description: 'Serious complication', cssClass: 'sev-4', order: 4 },
    { key: 'code-5', label: 'Code 5', value: 'Code 5', description: 'Critical / legal',     cssClass: 'sev-5', order: 5 },
    // reviewOnly: NOT offered at submission — an admin assigns it during review to
    // reclassify a case as not an adverse event, so it can be tracked separately.
    { key: 'not-ae', label: 'Not AE', value: 'Not AE', description: 'Reviewed and classified as not an adverse event', cssClass: '', order: 6, reviewOnly: true },
  ],
  statusValues: [
    { key: 'new',      label: 'New',                   value: 'New',                   cssClass: 'status-new',  isActive: true,  order: 1 },
    { key: 'open',     label: 'Open',                  value: 'Open',                  cssClass: 'status-open', isActive: true,  order: 2 },
    // key stays 'triage' — it is the mergeByKey identity against any statusValues
    // list ever published to admin_config (renamed to Pending by migration 017);
    // a new key would leave a duplicate row behind after the merge.
    { key: 'triage',   label: 'Pending',               value: 'Pending',               cssClass: 'status-inv',  isActive: true,  order: 3 },
    { key: 'awaiting', label: 'Waiting on Client', value: 'Awaiting Client Input', cssClass: 'status-wait', isActive: true,  order: 4 },
    { key: 'closed',   label: 'Closed',                value: 'Closed',                cssClass: 'status-res',  isActive: false, order: 5 },
  ],
  treatmentTypes: [
    { key: 'lhr',   label: 'LHR',   value: 'LHR',   description: 'Laser Hair Removal',   aliases: ['LHR', 'LASER', 'LASER HAIR REMOVAL'],                             subtypes: [],                 cssClass: 'tx-lhr', order: 1 },
    { key: 'awi',   label: 'AWI',   value: 'AWI',   description: 'Advanced Injectables', aliases: ['AWI', 'ADVANCED INJECTABLES', 'INJECTABLE', 'BOTOX', 'FILLER'],   subtypes: ['Toxin', 'Filler'], cssClass: 'tx-awi', order: 2 },
    // ── New treatment types from the AE report workbook (2026-07) ─────────────
    // Flat treatments (no subtypes). Conditions live in eventVocabularies.js
    // (INCIDENT_CATEGORIES), surfaced under every code as a placeholder until
    // severity coding is signed off. cssClass '' = neutral badge — only tx-lhr /
    // tx-awi swatches exist today (see BADGE_CSS in configSchema.js), so keep ''
    // to stay valid if these rows are ever published via the Admin panel.
    { key: 'skin-peels',      label: 'Skin Peels',                     value: 'SkinPeels',      description: 'Chemical / skin peels',                       aliases: ['PEEL', 'PEELS', 'SKIN PEEL'],               subtypes: [], cssClass: '', order: 3  },
    { key: 'skin-treatment',  label: 'Skin Treatment',                 value: 'SkinTreatment',  description: 'General skin treatments',                     aliases: ['SKIN', 'SKIN TREATMENT'],                   subtypes: [], cssClass: '', order: 4  },
    { key: 'potenza',         label: 'Potenza / Morpheus 8',           value: 'Potenza',        description: 'RF microneedling (Potenza / Morpheus 8)',     aliases: ['POTENZA', 'MORPHEUS', 'MORPHEUS 8'],        subtypes: [], cssClass: '', order: 5  },
    { key: 'hydrafacial',     label: 'Hydrafacial',                    value: 'Hydrafacial',    description: 'Hydrafacial',                                 aliases: ['HYDRAFACIAL'],                              subtypes: [], cssClass: '', order: 6  },
    { key: 'micro-needling',  label: 'Micro-needling',                 value: 'MicroNeedling',  description: 'Micro-needling',                              aliases: ['MICRONEEDLING', 'MICRO-NEEDLING'],          subtypes: [], cssClass: '', order: 7  },
    { key: 'tattoo-removal',  label: 'Tattoo Removal',                 value: 'TattooRemoval',  description: 'Laser tattoo removal',                        aliases: ['TATTOO', 'TATTOO REMOVAL'],                 subtypes: [], cssClass: '', order: 8  },
    { key: 'coolsculpt',      label: 'CoolSculpt',                     value: 'CoolSculpt',     description: 'Cryolipolysis (CoolSculpt)',                  aliases: ['COOLSCULPT', 'CRYOLIPOLYSIS'],              subtypes: [], cssClass: '', order: 9  },
    { key: 'exilis',          label: 'Exilis',                         value: 'Exilis',         description: 'Exilis',                                      aliases: ['EXILIS'],                                   subtypes: [], cssClass: '', order: 10 },
    { key: 'emsculpt',        label: 'Emsculpt',                       value: 'Emsculpt',       description: 'Emsculpt',                                    aliases: ['EMSCULPT'],                                 subtypes: [], cssClass: '', order: 11 },
    { key: 'clear-brilliant', label: 'Clear and Brilliant',            value: 'ClearBrilliant', description: 'Clear + Brilliant laser',                     aliases: ['CLEAR AND BRILLIANT', 'CLEAR + BRILLIANT'], subtypes: [], cssClass: '', order: 12 },
    { key: 'emsella',         label: 'Emsella',                        value: 'Emsella',        description: 'Emsella',                                     aliases: ['EMSELLA'],                                  subtypes: [], cssClass: '', order: 13 },
    // The "all treatments" cross-cutting conditions are handled as tags, not a
    // treatment type — so no AllTreatments row here.
    { key: 'other', label: 'Other', value: 'Other', description: 'Other treatments',     aliases: [],                                                                  subtypes: [],                 cssClass: '',       order: 99 },
  ],
  // Incident-category cascade (Phase 3) — seeded from eventVocabularies.js so
  // the hardcoded fallback and the config stay identical until admin edits it.
  // LHR keyed by code; AWI keyed by subtype then code.
  incidentCategoriesByTreatmentAndCode: INCIDENT_CATEGORIES,
  legalThresholdMonths: 6,

  // Event types — the discriminator that gates the new-case form. `value` is a
  // fixed enum aligned with the API/DB event_type column; admin edits
  // label / hint / enabled / order only (a new type needs a code-side form).
  eventTypes: [
    { key: 'treatment',       value: 'Treatment',      label: 'Treatment incident', hint: 'Adverse event arising from a clinical treatment',     enabled: true,  order: 1 },
    { key: 'near-miss',       value: 'NearMiss',       label: 'Near miss',          hint: 'A hazard or near-incident caught before harm occurred', enabled: true,  order: 2 },
    { key: 'clinic-incident', value: 'ClinicIncident', label: 'Clinic incident',    hint: 'Non-treatment incident at a clinic — injury, accident, or public liability', enabled: true,  order: 3 },
  ],

  // Per-case requirement types — the picker on the case Notes panel. Editable in
  // AdminPanel (Case Config). The case picker always appends "Other (specify)".
  // Derived from the SHARED single-source constant (the same list the API
  // validates an add against) so the key/label pairs can never drift between
  // the SPA seed and the server allowlist. `order` is added here for the
  // AdminPanel editor's sort; the shared list intentionally stays UI-agnostic.
  requirementTypes: DEFAULT_REQUIREMENT_TYPES.map((t, i) => ({ ...t, order: i + 1 })),

  // ── Notifications ───────────────────────────────────────────────────────────
  // Custom flow rules (trigger + condition → recipient by role/job-title). Empty
  // by default; admins author them in Admin ▸ Notifications.
  notificationRules: [],
  // The built-in "new case submitted" notification — migrated from the
  // NOTIFY_ON_SUBMIT_EMAILS env var into editable config.
  caseSubmittedNotification: {
    enabled: true,
    recipients: [{ kind: 'email', value: 'mariusc@therapieclinic.com' }],
  },
  // Per-treatment extra recipients on the new-case notification — seeded from the
  // shared catalog hardcode so the default matches current behaviour; editable in
  // Admin ▸ Notifications.
  treatmentSubmitNotify: Object.entries(TREATMENT_SUBMIT_NOTIFY).map(([treatment, emails]) => ({
    treatment,
    recipients: emails.map(value => ({ kind: 'email', value })),
  })),
  // Named recipient groups — [{ key, label, emails[] }] — referenced by
  // { kind: 'group', value: <key> } recipient specs (an unknown key soft-fails
  // to zero addresses at send time). Editable in Admin ▸ Notifications.
  recipientGroups: [],
  // Master switch for the daily scheduled-notification scan. Off by default:
  // scheduled rules stay saved but never send until an admin enables this.
  scheduledNotificationsEnabled: false,

  // ── Code 4/5 response SLA (sla_escalation_logic.md) ─────────────────────────
  // Seeded from the SHARED defaults so the SPA chip, the Case Config editor and
  // the API engine agree byte-for-byte. slaEnabled is the kill switch (off).
  slaEnabled:               SLA_DEFAULTS.slaEnabled,
  slaHoursPerLeg:           SLA_DEFAULTS.slaHoursPerLeg,
  slaHoursPerLegLow:        SLA_DEFAULTS.slaHoursPerLegLow,
  slaHqCalendar:            SLA_DEFAULTS.slaHqCalendar,
  slaClinicDefaultCalendar: SLA_DEFAULTS.slaClinicDefaultCalendar,
  slaSides:                 SLA_DEFAULTS.slaSides,

  // ── Access & Roles ────────────────────────────────────────────────────────────
  // Built-in entries (except SuperAdmin) carry capabilities + scope here so
  // SuperAdmins can tune them in Admin ▸ Roles. SuperAdmin stays implicit only.
  roleDefinitions: [
    seededBuiltInRole('supervisor',      'Supervisor',           'Supervisor',     'Admin + cost feature (view + add)'),
    seededBuiltInRole('supervisor-view', 'Supervisor (view)',    'SupervisorView', 'Read-only of all cases + cost view — no comments, no edits'),
    seededBuiltInRole('admin',           'Administrator',        'Admin',          'Full case access — all cases, all comments including private (no cost feature)'),
    seededBuiltInRole('horo',            'Head of RO',           'HoRO',           'All cases in assigned region, all comments'),
    seededBuiltInRole('cm',              'Clinic Manager',       'ClinicManager',  'Cases for assigned clinic only, public comments'),
    seededBuiltInRole('ro',              'Read Only',            'ReadOnly',       'View cases and public comments only, no write access'),
  ],
  // NOTE: superAdminEmails here is config documentation only.
  // The live gate uses SUPER_ADMIN_EMAILS in AuthContext.jsx and requires a code change.
  superAdminEmails: ['mariusc@therapieclinic.com'],

  // Temporary, additive per-user clinic-access grants (SuperAdmin-authored in
  // Admin ▸ Users & Security). Each grant WIDENS one user's clinic scope until
  // its expiry; enforcement is server-side (scope.js extraClinics). Empty by
  // default. Shape: { id, email, clinics[], expiresAt, grantedBy, grantedAt, note? }.
  clinicAccessGrants: [],

  // ── Cost feature ──────────────────────────────────────────────────────────────
  // See cost_logic.md. Seeds; admin panel (step 4) edits live values.
  costCategories: [
    { key: 'refund',     label: 'Refund',                          cssClass: 'cost-refund',       order: 10, archived: false },
    { key: 'treatment',  label: 'Treatment / corrective',          cssClass: 'cost-corrective',   order: 20, archived: false },
    { key: 'medical',    label: 'Medical / consultant',            cssClass: 'cost-medical',      order: 30, archived: false },
    { key: 'legal',      label: 'Legal fees',                      cssClass: 'cost-legal',        order: 40, archived: false },
    { key: 'settlement', label: 'Settlement sum',                  cssClass: 'cost-compensation', order: 50, archived: false },
    { key: 'goodwill',   label: 'Goodwill',                        cssClass: 'cost-goodwill',     order: 60, archived: false },
    { key: 'other',      label: 'Other',                           cssClass: 'cost-other',        order: 99, archived: false },
  ],
  currencies: [
    { code: 'GBP', symbol: '£', label: 'Pound Sterling', isDefault: true,  archived: false },
    { code: 'EUR', symbol: '€', label: 'Euro',           isDefault: false, archived: false },
    { code: 'USD', symbol: '$', label: 'US Dollar',      isDefault: false, archived: false },
  ],

  // ── UI Settings ───────────────────────────────────────────────────────────────
  defaultPageSize: 20,
  defaultSort: { col: 'reportedDate', dir: 'desc' },
  visibleFilterGroups: { status: true, treatment: true, severity: true, country: true, clinic: true },
};

const AppConfigContext = createContext(null);

/** Ensure newer default severity codes (e.g. Code 5) appear even when DB config predates them. */
function mergeSeverityCodes(fromDb, defaults) {
  if (!Array.isArray(fromDb)) return defaults;
  const byKey = new Map(defaults.map(c => [c.key, c]));
  for (const c of fromDb) byKey.set(c.key, c);
  if (![...byKey.values()].some(c => c.value === 'Code 5')) {
    const code5 = defaults.find(c => c.value === 'Code 5');
    if (code5) byKey.set(code5.key, code5);
  }
  return [...byKey.values()].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

/** Generic key-merge: DB items override defaults; new default keys still appear. */
function mergeByKey(fromDb, defaults) {
  if (!Array.isArray(fromDb)) return defaults;
  const byKey = new Map(defaults.map(d => [d.key, d]));
  for (const d of fromDb) byKey.set(d.key, d);
  return [...byKey.values()].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

/** Currencies merge (keyed on `code`): a published DB entry wins per code, but
 *  new code-default currencies (e.g. USD for the US clinics) still surface even
 *  when the DB holds an older set — same additive rationale as treatmentTypes. */
function mergeCurrencies(fromDb, defaults) {
  if (!Array.isArray(fromDb)) return defaults;
  const byCode = new Map(defaults.map(c => [c.code, c]));   // defaults first → keeps GBP, EUR, USD order
  for (const c of fromDb) if (c && c.code) byCode.set(c.code, c);
  return [...byCode.values()];
}

/**
 * roleDefinitions merge: key-merge like the other vocab lists, then re-assert
 * `builtIn` on the seeded rows — pre-governance DB rows (saved by the old
 * display-only Users & Security editor) predate the flag and must never
 * masquerade as custom roles.
 */
function mergeRoleDefinitions(fromDb, defaults) {
  const builtInKeys = new Set(defaults.map(d => d.key));
  return mergeByKey(fromDb, defaults).map(d => {
    const merged = builtInKeys.has(d.key) ? { ...d, builtIn: true } : d;
    if (!merged.builtIn || merged.value === 'SuperAdmin') return merged;
    const seed = defaults.find(x => x.key === merged.key);
    return {
      ...merged,
      enabled: merged.enabled !== false,
      priority: merged.priority ?? seed?.priority,
      capabilities: merged.capabilities || seed?.capabilities,
      scope: merged.scope
        || seed?.scope
        || (merged.value === 'HoRO' ? { type: 'assignedRegion' }
          : merged.value === 'ClinicManager' ? { type: 'assignedClinics' }
            : { type: 'all' }),
    };
  });
}

// ── roleDefinitions bridge to AuthContext ─────────────────────────────────────
// AuthContext mounts ABOVE this provider (AuthProvider wraps App in main.jsx;
// AppConfigProvider sits inside App), so it cannot call useAppConfig(). Custom-
// role capability resolution needs the governed roleDefinitions, so the merged
// value is published through this tiny module-level store instead. null = not
// resolved yet; after load — including load FAILURE, which falls back to the
// built-in seed — it is always an array, which is how AuthContext tells
// "still waiting" apart from "config resolved, role definitely unknown".
let _roleDefinitions = null;
const _roleDefListeners = new Set();

export function getLoadedRoleDefinitions() { return _roleDefinitions; }

export function subscribeRoleDefinitions(listener) {
  _roleDefListeners.add(listener);
  return () => _roleDefListeners.delete(listener);
}

function publishRoleDefinitions(defs) {
  _roleDefinitions = Array.isArray(defs) ? defs : DEFAULT_CONFIG.roleDefinitions;
  for (const fn of _roleDefListeners) fn(_roleDefinitions);
}

/**
 * Treatment-level merge for the incident-category tree: the DB tree wins for any
 * treatment it defines (respecting admin edits to LHR/AWI), while treatment
 * branches that exist ONLY in the code default (e.g. newly-added workbook
 * treatments the DB hasn't published yet) are still surfaced. Mirrors the
 * mergeByKey rationale so a new default treatment appears without a DB re-publish.
 */
function mergeCategoryTree(fromDb, defaults) {
  if (!fromDb || typeof fromDb !== 'object' || Array.isArray(fromDb)) return defaults;
  return { ...defaults, ...fromDb };
}

// LHR Code 1 submission categories are code-owned (PFO removed for everyone).
// Code 2 keeps the full vocabulary in config; reporters see a subset via
// filterLhrReporterCategories in the wizard / case editor.
function applyLhrSubmissionCategories(tree, defaults) {
  const lhr = defaults?.LHR;
  if (!tree || !lhr) return tree;
  if (!tree.LHR) return tree;
  return {
    ...tree,
    LHR: {
      ...tree.LHR,
      'Code 1': lhr['Code 1'],
    },
  };
}

/** Keep the "Other" treatment pinned to the END of the list, whatever its stored order. */
function pinOtherLast(list) {
  if (!Array.isArray(list)) return list;
  return [...list].sort((a, b) => {
    const ao = a?.value === 'Other' ? Infinity : (a?.order ?? 0);
    const bo = b?.value === 'Other' ? Infinity : (b?.order ?? 0);
    return ao - bo;
  });
}

export function useAppConfig() {
  const ctx = useContext(AppConfigContext);
  if (!ctx) throw new Error('useAppConfig must be used inside <AppConfigProvider>');
  return ctx;
}

// Server config map → the app's config (code defaults filled in, additive merges).
function mergeServerConfig(sp) {
  const merged = { ...DEFAULT_CONFIG };
  for (const key of Object.keys(DEFAULT_CONFIG)) {
    if (sp[key] !== undefined) merged[key] = sp[key];
  }
  merged.severityCodes = mergeSeverityCodes(sp.severityCodes, DEFAULT_CONFIG.severityCodes);
  merged.eventTypes    = mergeByKey(sp.eventTypes, DEFAULT_CONFIG.eventTypes);
  merged.roleDefinitions = mergeRoleDefinitions(sp.roleDefinitions, DEFAULT_CONFIG.roleDefinitions);
  // Additive merges so treatment types / category branches added in code
  // (e.g. the 2026-07 workbook treatments) still surface even when the DB
  // has an older published set — DB rows/branches keep winning per key.
  merged.treatmentTypes = pinOtherLast(mergeByKey(sp.treatmentTypes, DEFAULT_CONFIG.treatmentTypes));
  merged.currencies = mergeCurrencies(sp.currencies, DEFAULT_CONFIG.currencies);
  // Requirement types too: the 0.46 code defaults (training-certificate,
  // post-photo — the two-step metadata slots as per-case asks) must reach
  // a tenant whose DB holds the older published list. DB rows win per key.
  merged.requirementTypes = mergeByKey(sp.requirementTypes, DEFAULT_CONFIG.requirementTypes);
  merged.incidentCategoriesByTreatmentAndCode = applyLhrSubmissionCategories(
    mergeCategoryTree(
      sp.incidentCategoriesByTreatmentAndCode,
      DEFAULT_CONFIG.incidentCategoriesByTreatmentAndCode,
    ),
    DEFAULT_CONFIG.incidentCategoriesByTreatmentAndCode,
  );
  // Default `subtypes` on any legacy treatment row so the declared field is
  // ALWAYS present — the rest of the app (editor, validator, new-case
  // cascade) then reads that one source of truth, never tree-shape guesses.
  merged.treatmentTypes = migrateSubtypeDeclarations(
    merged.treatmentTypes,
    merged.incidentCategoriesByTreatmentAndCode,
    merged.severityCodes,
  );
  return merged;
}

export function AppConfigProvider({ children }) {
  const [config, setConfig]      = useState(DEFAULT_CONFIG);
  const [loading, setLoading]    = useState(true);
  const [saveError, setSaveError] = useState(null);
  // Per-key version the loaded config came from (stale-publish guard): sent as
  // baseVersion on save so a publish from an outdated page is refused (409)
  // instead of overwriting someone else's newer change. null = server too old.
  const versionsRef = useRef(null);
  const baseVersionOf = (key) => (versionsRef.current ? (versionsRef.current[key] ?? null) : undefined);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setBootStage('config.load', 'Loading app configuration');
      try {
        const sp = await getAdminConfig();
        if (cancelled) return;
        const merged = mergeServerConfig(sp);
        versionsRef.current = sp.__versions || null;
        setConfig(merged);
        publishRoleDefinitions(merged.roleDefinitions);
        setBootStage('config.ready', 'Configuration loaded', 'From server');
      } catch (err) {
        console.warn('[AppConfig] config load failed — using defaults:', err.message);
        // Fail closed for custom roles: the seed has no custom entries, so an
        // unknown role string resolves to Unauthorized rather than waiting forever.
        publishRoleDefinitions(DEFAULT_CONFIG.roleDefinitions);
        setBootStage('config.ready', 'Configuration loaded', `Defaults only (${err.message})`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  // Plain saves update the UI optimistically and roll back on failure. A
  // publish (governed key) only lands in local config once the server accepts
  // it: the optimistic write + rollback looked to editors like someone else's
  // publish, and the Roles draft-sync then re-added roles the user had just
  // deleted (Bugbot on PR #94).
  async function updateConfig(key, value, opts = {}) {
    const prev = config[key];
    const optimistic = !opts.publish;
    if (optimistic) setConfig(c => ({ ...c, [key]: value }));
    setSaveError(null);
    try {
      const r = await saveAdminConfigKey(key, value, { ...opts, baseVersion: baseVersionOf(key) });
      if (versionsRef.current && r && r.version !== undefined) versionsRef.current[key] = r.version;
      if (!optimistic) setConfig(c => ({ ...c, [key]: value }));
      if (key === 'roleDefinitions') publishRoleDefinitions(value);
    } catch (err) {
      if (optimistic) setConfig(c => ({ ...c, [key]: prev }));
      setSaveError(err.message);
      throw err;
    }
  }

  // Atomic multi-key publish. `entries` is [{ key, value }]. On success the
  // local config is updated for every key; on failure ALL keys roll back (the
  // server transaction is all-or-nothing) and we restore the previous values.
  async function updateConfigBatch(entries, opts = {}) {
    const prev = {};
    for (const { key } of entries) prev[key] = config[key];
    const optimistic = !opts.publish;
    const apply = () => setConfig(c => {
      const next = { ...c };
      for (const { key, value } of entries) next[key] = value;
      return next;
    });
    if (optimistic) apply();
    setSaveError(null);
    try {
      const r = await saveAdminConfigBatch(entries.map(e => ({ ...e, baseVersion: baseVersionOf(e.key) })), opts);
      if (versionsRef.current && r?.versions) Object.assign(versionsRef.current, r.versions);
      if (!optimistic) apply();
      for (const { key, value } of entries) {
        if (key === 'roleDefinitions') publishRoleDefinitions(value);
      }
    } catch (err) {
      if (optimistic) setConfig(c => ({ ...c, ...prev }));
      setSaveError(err.message);
      throw err;
    }
  }

  // Re-read config from the server (after a stale-publish refusal). Unsaved
  // drafts live in the editors, so they survive; their saved baseline moves on.
  async function reloadConfig() {
    const sp = await getAdminConfig();
    const merged = mergeServerConfig(sp);
    versionsRef.current = sp.__versions || null;
    setConfig(merged);
    setSaveError(null);
    publishRoleDefinitions(merged.roleDefinitions);
    return merged;
  }

  return (
    <AppConfigContext.Provider value={{ config, loading, updateConfig, updateConfigBatch, reloadConfig, saveError }}>
      {children}
    </AppConfigContext.Provider>
  );
}
